import { useMemo } from 'react';
import {
  PerformanceReviewExportType,
  useExportHistoryListPerformanceReviewQuery,
  useExportProgressPerformanceReviewSubscription,
} from '@/common/graphql';
import type { ExportHistoryKind, UseExportHistoryResult } from './types';
import { toPerformanceReviewExportHistoryRow } from './types';

const PERFORMANCE_REVIEW_TYPES_BY_KIND: Record<
  'performanceReviewList' | 'performanceReviewResults',
  PerformanceReviewExportType[]
> = {
  performanceReviewList: [PerformanceReviewExportType.List, PerformanceReviewExportType.Details],
  performanceReviewResults: [PerformanceReviewExportType.Results],
};

/**
 * Query + Subscription + row-mapping for `kind: 'performanceReviewList' | 'performanceReviewResults'`.
 * Always called - internally no-ops via `skip` when inactive.
 */
export function useExportHistoryPerformanceReview(
  kind: ExportHistoryKind,
  onlyMyTask: boolean,
): UseExportHistoryResult {
  const skip = kind !== 'performanceReviewList' && kind !== 'performanceReviewResults';
  const types = useMemo(
    () =>
      kind === 'performanceReviewList' || kind === 'performanceReviewResults'
        ? PERFORMANCE_REVIEW_TYPES_BY_KIND[kind]
        : [],
    [kind],
  );

  const { data, loading, error, refetch } = useExportHistoryListPerformanceReviewQuery({
    variables: { onlyMyTask, types },
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const history = useMemo(
    () => (data?.exportHistoryList ?? []).map(toPerformanceReviewExportHistoryRow),
    [data?.exportHistoryList],
  );

  const { data: progressData } = useExportProgressPerformanceReviewSubscription({ variables: { types }, skip });
  const progress = useMemo(() => progressData?.exportProgress ?? [], [progressData?.exportProgress]);

  return { error, history, isActive: !skip, loading, progress, refetch };
}
